import { useMedia } from "react-use"
import { create } from "zustand"
import { persist } from "zustand/middleware"

import type { CopilotScope } from "@productlane/lib/src/ai"
import { copilotScopeKey } from "@productlane/lib/src/ai"

import { useAuthState } from "./auth-state"

// Open/close state for the copilot on the docs, changelog, and inbox surfaces.
// Thread pages keep their own tab state in `useThreadSidebarState`; this store
// drives the standalone `CopilotOverlay`. Open state is the surface preference
// itself: a panel is open whenever its surface is, so moving to the next
// article or entry never closes and reopens it (which read as a stutter).
interface CopilotState {
	// A message queued to send as the copilot's first turn when it opens (e.g.
	// the inbox empty-state input). Consumed and cleared by the overlay.
	seedMessage: string | null
	// Set only when the user actively opens a copilot, so its composer takes
	// focus then but not when a page or thread opens with the copilot already
	// active. Keyed by scope, so a request never lands on another surface.
	pendingFocusScopeKey: string | null
	// Like the focus request, but the composer starts recording instead.
	pendingVoiceScopeKey: string | null
	// Last sidebar chat session per remembered copilot scope (the inbox).
	// Persisted so reload reopens the same chat.
	rememberedSessionIds: Record<string, string>
	// Whether the copilot should be open, keyed by surface rather than by the
	// individual article or entry: hiding it on one doc keeps it hidden on the
	// next. Missing keys default to open for docs and changelog, closed for the
	// inbox.
	surfaceOpenState: Record<string, boolean>

	openWith: (scope: CopilotScope, seedMessage?: string) => void
	openWithVoice: (scope: CopilotScope) => void
	consumeVoiceRequest: () => void
	close: (scope: CopilotScope) => void
	toggle: (scope: CopilotScope) => void
	isOpenFor: (scope: CopilotScope) => boolean
	getRememberedSessionId: (scope: CopilotScope) => string | null
	setRememberedSessionId: (
		scope: CopilotScope,
		sessionId: string | null,
	) => void
	consumeSeed: () => void
	// For copilots whose open state lives elsewhere (the thread right-bar tab).
	requestFocus: (scope: CopilotScope) => void
	consumeFocusRequest: () => void
}

export function isStandaloneCopilotScope(
	scope: CopilotScope,
): scope is
	| { kind: "workspace" }
	| { kind: "reporting" }
	| { kind: "agent" }
	| { kind: "article"; articleId: string }
	| { kind: "changelog"; changelogId: string } {
	return (
		scope.kind === "workspace" ||
		scope.kind === "reporting" ||
		scope.kind === "agent" ||
		scope.kind === "article" ||
		scope.kind === "changelog"
	)
}

// Below this width the copilot fills the screen instead of sitting beside the
// editor, so it opens on an explicit tap rather than on its own.
export const COPILOT_SHEET_QUERY = "(max-width: 639px)"

export function isCopilotSheetViewport(): boolean {
	if (typeof window === "undefined" || !window.matchMedia) return false
	return window.matchMedia(COPILOT_SHEET_QUERY).matches
}

function defaultOpenForScope(scope: CopilotScope, isSheet: boolean): boolean {
	if (isSheet) return false
	return (
		scope.kind === "article" ||
		scope.kind === "changelog" ||
		scope.kind === "reporting" ||
		scope.kind === "agent"
	)
}

// Docs and changelog copilots start a fresh chat every time their page opens,
// so moving to another article or entry never resurrects an old conversation.
export function copilotScopeStartsFreshChat(scope: CopilotScope): boolean {
	return (
		scope.kind === "article" ||
		scope.kind === "changelog" ||
		(scope.kind === "agent" && !!(scope.recommendationId || scope.chatId))
	)
}

// Only the inbox copilot reopens the conversation the user left behind.
export function shouldRememberCopilotSession(scope: CopilotScope): boolean {
	return isStandaloneCopilotScope(scope) && !copilotScopeStartsFreshChat(scope)
}

// Docs share one preference, changelog another, the inbox its own. The sheet
// keeps its own keys: leaving the side panel open on a desktop must not make a
// phone cover the article on every navigation.
export const COPILOT_SHEET_KEY_SUFFIX = ":sheet"

function copilotSurfaceKey(scope: CopilotScope, isSheet: boolean): string {
	return isSheet ? `${scope.kind}${COPILOT_SHEET_KEY_SUFFIX}` : scope.kind
}

export function isCopilotOpenFor(
	surfaceOpenState: Record<string, boolean>,
	scope: CopilotScope,
	isSheet: boolean = isCopilotSheetViewport(),
): boolean {
	const remembered = surfaceOpenState[copilotSurfaceKey(scope, isSheet)]
	if (typeof remembered === "boolean") return remembered
	return defaultOpenForScope(scope, isSheet)
}

// Subscribes to the preference rather than reading it once, so a toggle
// re-renders the panel while a route change does not.
export function useIsCopilotOpen(scope: CopilotScope): boolean {
	const surfaceOpenState = useCopilotState((s) => s.surfaceOpenState)
	const isSheet = useMedia(COPILOT_SHEET_QUERY, false)
	return isCopilotOpenFor(surfaceOpenState, scope, isSheet)
}

function setSurfaceOpen(
	state: Pick<CopilotState, "surfaceOpenState">,
	scope: CopilotScope,
	isOpen: boolean,
): Record<string, boolean> {
	return {
		...state.surfaceOpenState,
		[copilotSurfaceKey(scope, isCopilotSheetViewport())]: isOpen,
	}
}

// The inbox copilot is opened deliberately, so a reload lands on the greeting again rather than on an open panel.
export function stripSessionOnlySurfaces(
	surfaceOpenState: Record<string, boolean>,
): Record<string, boolean> {
	return Object.fromEntries(
		Object.entries(surfaceOpenState).filter(
			([key]) => key.split(COPILOT_SHEET_KEY_SUFFIX)[0] !== "workspace",
		),
	)
}

// A session belongs to one workspace, so a remembered id never follows the user into another.
function rememberedSessionKey(scope: CopilotScope): string {
	const workspaceId = useAuthState.getState().data?.workspaceId ?? ""
	return `${workspaceId}:${copilotScopeKey(scope)}`
}

export const useCopilotState = create<CopilotState>()(
	persist(
		(set, get) => ({
			seedMessage: null,
			pendingFocusScopeKey: null,
			pendingVoiceScopeKey: null,
			rememberedSessionIds: {},
			surfaceOpenState: {},

			openWith: (scope, seedMessage) =>
				set({
					seedMessage: seedMessage ?? null,
					pendingFocusScopeKey: copilotScopeKey(scope),
					surfaceOpenState: setSurfaceOpen(get(), scope, true),
				}),

			openWithVoice: (scope) =>
				set({
					seedMessage: null,
					pendingVoiceScopeKey: copilotScopeKey(scope),
					surfaceOpenState: setSurfaceOpen(get(), scope, true),
				}),

			consumeVoiceRequest: () => set({ pendingVoiceScopeKey: null }),

			close: (scope) =>
				set({
					seedMessage: null,
					pendingFocusScopeKey: null,
					pendingVoiceScopeKey: null,
					surfaceOpenState: setSurfaceOpen(get(), scope, false),
				}),

			toggle: (scope) => {
				if (get().isOpenFor(scope)) {
					set({
						seedMessage: null,
						pendingFocusScopeKey: null,
						pendingVoiceScopeKey: null,
						surfaceOpenState: setSurfaceOpen(get(), scope, false),
					})
					return
				}
				set({
					seedMessage: null,
					pendingFocusScopeKey: copilotScopeKey(scope),
					surfaceOpenState: setSurfaceOpen(get(), scope, true),
				})
			},

			isOpenFor: (scope) => isCopilotOpenFor(get().surfaceOpenState, scope),

			getRememberedSessionId: (scope) =>
				get().rememberedSessionIds[rememberedSessionKey(scope)] ?? null,

			setRememberedSessionId: (scope, sessionId) => {
				const key = rememberedSessionKey(scope)
				if (sessionId === null) {
					const { [key]: _removed, ...rest } = get().rememberedSessionIds
					set({ rememberedSessionIds: rest })
					return
				}
				set({
					rememberedSessionIds: {
						...get().rememberedSessionIds,
						[key]: sessionId,
					},
				})
			},

			consumeSeed: () => set({ seedMessage: null }),

			requestFocus: (scope) =>
				set({ pendingFocusScopeKey: copilotScopeKey(scope) }),

			consumeFocusRequest: () => set({ pendingFocusScopeKey: null }),
		}),
		{
			name: "copilot-state",
			version: 1,
			// Version 0 keyed sessions by scope alone, so they could point at another workspace.
			migrate: (persisted, version) =>
				(version < 1
					? { ...(persisted as object), rememberedSessionIds: {} }
					: persisted) as CopilotState,
			partialize: (state) => ({
				rememberedSessionIds: state.rememberedSessionIds,
				surfaceOpenState: stripSessionOnlySurfaces(state.surfaceOpenState),
			}),
		},
	),
)

export function whenCopilotStateHydrated(): Promise<void> {
	if (useCopilotState.persist.hasHydrated()) return Promise.resolve()
	return new Promise((resolve) => {
		const unsub = useCopilotState.persist.onFinishHydration(() => {
			unsub()
			resolve()
		})
	})
}
